import * as React from "react";
import styled from "@emotion/styled";
import {
  AppError,
  Box,
  Button,
  Checkbox,
  Container,
  DatePicker,
  FieldError,
  Fieldset,
  FlexRow,
  Form,
  Hr,
  Input,
  Label,
  Legend,
  RequiredText,
  RichTextEditor,
  Select,
  Avatar,
  H2,
  Link,
} from "atoms";
import { unitMap } from "atoms/Currency";
import { ImageUpload } from "features/images";
import { useArtist } from "features/reviewable";
import { SuccessMessage } from "features/reviewable/components/ArtistForm";
import { fieldOptions, validators } from "utils/form";
import { useAuth } from "features/auth";
import type { Post, PostType, SaleTag } from "../types";
import { usePostForm } from "../api/usePosts";

// NOTE: Posts can only have ONE feed destination at the moment. When admin is entering an artist
// ID into the form, it should be at this index.
const destIndex = 0;

const textCharLimit = 1000;

const postTypeButtons: { value: PostType; label: string }[] = [
  { value: "SALE", label: "Item For Sale" },
  { value: "ANNOUNCEMENT", label: "Announcement" },
  { value: "GIVEAWAY", label: "Giveaway" },
  { value: "EVENT", label: "Event" },
];

const saleTagOptions: { value: SaleTag; label?: string }[] = [
  { value: "1/1" },
  { value: "Art Print" },
  { value: "Auction" },
  { value: "Collection" },
  { value: "Limited Edition" },
  { value: "Merchandise" },
  { value: "Open Edition" },
  { value: "Physical Art" },
];

const saleCurrencyOptions = Object.entries(unitMap);

type Props = {
  artistId?: string;
  post?: Post;
  onSuccess?: Parameters<typeof usePostForm>[0]["onSuccess"];
};

function PostForm({ post, artistId, onSuccess }: Props) {
  const isEdit = !!post;
  const {
    register,
    onSubmit,
    formState: { errors },
    mutation: { isLoading, isSuccess, isError, error },
    setValue,
    setError,
    clearErrors,
    watch,
  } = usePostForm({ post, artistId, onSuccess });

  const postType = watch("postType");
  const setPostType = (newPostType: PostType) => () => {
    setValue("postType", newPostType === postType ? "OTHER" : newPostType);
    // TODO: delete fields that don't match new type? or save values in case reselect and delete onSubmit?
  };

  const requiredForSaleType = (value?: string) =>
    postType !== "SALE" || !!value || "This field is required";

  // Watch saleStart date for saleEnd date validation
  const saleStart = watch("saleStart");
  // Watch salePrice value for saleCurrency validation
  const salePrice = watch("salePrice");
  const currencyValidation = (val?: string) =>
    !salePrice || !!val || "This field is required if a price is entered";

  const { data } = useArtist(artistId);

  const {
    roles: { isAdmin },
  } = useAuth();

  const onRichTextChange: React.ComponentProps<
    typeof RichTextEditor
  >["onChange"] = (html, _, __, editor) => {
    setValue("text", html);

    // TODO: move this out of onChange and into onSubmit like other validation
    // this could slow things down, plus there's a weird focus state if you are typing and go
    // over the character limit
    const isOverLimit = editor.getLength() > textCharLimit;
    if (isOverLimit && !errors.text) {
      setError("text", {
        type: "custom",
        message: `This field is limited to ${textCharLimit} characters`,
      });
    } else if (!isOverLimit && errors.text) {
      clearErrors("text");
    }
  };

  // If no onSuccess handler is given to usePostForm to navigate away from form,
  // a button will show in place of submit button to reset form/mutation for another submission
  const reset = () => {
    // TODO: tried to reset both form and mutation here, but RichTextEditor is technically an
    // uncontrolled component and I don't have a way to access the editor state from the outside
    // As a hack, reload the window. Revisit if/when upgrade to ReactQuill v2 or roll own Quill instance
    // form.reset();
    // mutation.reset();
    window.location.reload();
  };

  return (
    <Container width="xs">
      <Box style={{ width: "100%" }}>
        <SForm onSubmit={onSubmit}>
          {!artistId ? (
            <div>
              <Label>
                Artist ID / Slug
                <RequiredText />
                <Input
                  {...register(
                    `destinations.${destIndex}.feedId`,
                    fieldOptions.required
                  )}
                />
                {errors.destinations?.[destIndex]?.feedId && (
                  <FieldError>
                    {errors.destinations?.[destIndex]?.feedId.message}
                  </FieldError>
                )}
              </Label>
              <Input
                type="hidden"
                value="ARTIST"
                {...register(
                  `destinations.${destIndex}.feedType`,
                  fieldOptions.required
                )}
              />
              <Hr />
            </div>
          ) : (
            <FlexRow
              gap="7px"
              style={{
                margin: ".15em 0 0",
                paddingBottom: ".75em",
                borderBottom: "1px solid rgb(0 0 0 / .1)",
              }}
            >
              <Avatar src={data?.heroImage.url300} size="1.75em" />
              <H2 size="sm" style={{ margin: 0 }}>
                {data?.name}
              </H2>
            </FlexRow>
          )}

          <Fieldset>
            <Legend>Update Type</Legend>

            <FlexRow gap="0">
              {postTypeButtons.map(({ value, label }) => (
                <PostTypeButton
                  key={value}
                  onClick={setPostType(value)}
                  selected={value === postType}
                >
                  {label}
                </PostTypeButton>
              ))}
            </FlexRow>
          </Fieldset>

          <Label>
            {getTitleLabel(postType)}
            <RequiredText />
            <Input
              {...register("title", {
                ...fieldOptions.required,
                ...fieldOptions.maxLength(80),
              })}
            />
            {errors.title && <FieldError>{errors.title.message}</FieldError>}
          </Label>

          <Label>
            {getTextLabel(postType)}
            <RequiredText />
            <RichTextEditor
              onChange={onRichTextChange}
              placeholder="What do you want to say?"
              defaultValue={post?.text}
            />
            {errors.text && <FieldError>{errors.text.message}</FieldError>}
          </Label>

          <ImageUpload
            register={register}
            setValue={setValue}
            required={postType === "SALE"}
            fieldName="image"
            label={getImageLabel(postType)}
            errorMessage={errors?.image?.url?.message}
            src={post?.image}
          />

          {isEdit &&
            isAdmin &&
            post.image?.tags?.map((tag) => (
              <Checkbox
                label={`Remove Tag: ${tag}`}
                value={tag}
                {...register("image.tagOverrides")}
              />
            ))}

          <Label>
            {getLinkLabel(postType)}
            <Input
              {...register("link", {
                validate: {
                  validURL: validators.validURL,
                  required: requiredForSaleType,
                },
              })}
            />
            {errors.link && <FieldError>{errors.link.message}</FieldError>}
          </Label>

          {postType === "SALE" && (
            <>
              <TwoColumnForm style={{ paddingTop: "1em" }}>
                <Label>
                  Drop type
                  <RequiredText />
                  <Select {...register("saleTags", fieldOptions.required)}>
                    <option value="" aria-label="Choose drop type" disabled>
                      --
                    </option>
                    {saleTagOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label ?? value}
                      </option>
                    ))}
                  </Select>
                  {errors.saleTags && (
                    <FieldError>{errors.saleTags.message}</FieldError>
                  )}
                </Label>
                <div style={{ alignSelf: "end" }}>
                  <Checkbox
                    label="This is a New Drop"
                    {...register("newDrop")}
                    style={{ alignSelf: "start" }}
                  />
                </div>
              </TwoColumnForm>

              <TwoColumnForm>
                <Label>
                  Price
                  <Input
                    type="number"
                    placeholder="0.00"
                    step="any"
                    {...register("salePrice", { valueAsNumber: true })}
                  />
                </Label>

                <Label>
                  Currency
                  <Select
                    {...register("saleCurrency", {
                      validate: currencyValidation,
                    })}
                  >
                    <option value="" aria-label="Choose currency">
                      --
                    </option>
                    {saleCurrencyOptions.map(([key, value]) => (
                      <option value={key} key={key}>
                        {value.label}
                      </option>
                    ))}
                  </Select>
                  {errors.saleCurrency && (
                    <FieldError>{errors.saleCurrency.message}</FieldError>
                  )}
                </Label>
              </TwoColumnForm>
              <Fieldset>
                <TwoColumnForm>
                  <Label>
                    Start Date
                    <DatePicker {...register("saleStart")} />
                  </Label>

                  <Label>
                    End Date
                    <DatePicker
                      {...register("saleEnd", {
                        validate: validators.validEndDate(saleStart),
                      })}
                    />
                  </Label>
                </TwoColumnForm>
                {errors.saleEnd && (
                  <FieldError
                    style={{
                      gridColumnStart: 1,
                      gridColumnEnd: -1,
                      width: "100%",
                    }}
                  >
                    {errors.saleEnd.message}
                  </FieldError>
                )}
              </Fieldset>
            </>
          )}

          {isSuccess ? (
            <>
              <SSuccessMessage duration={6}>
                Your {postType !== "OTHER" ? postType.toLowerCase() : "update"}{" "}
                has been posted!
              </SSuccessMessage>

              {!isEdit && (
                <Button variant="secondary" size="sm" onClick={reset}>
                  Post Another?
                </Button>
              )}
            </>
          ) : (
            <FlexRow
              gap="10px"
              alignItems="flex-end"
              itemsFlex="1 0 155px"
              flexWrap="wrap"
            >
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={isLoading}
              >
                {isEdit ? "Save Changes" : "Post your Update"}
              </Button>
              {isEdit && (
                <Link
                  variant="secondary"
                  size="sm"
                  to={`/artists/${artistId}/updates/${post.id}`}
                >
                  Cancel
                </Link>
              )}
            </FlexRow>
          )}

          {isError && (
            <AppError>
              {error instanceof Error ? error.message : "Something went wrong"}
            </AppError>
          )}
        </SForm>
      </Box>
    </Container>
  );
}

const SForm = styled(Form)({
  gap: "1.25rem",
  "input, textarea, *:has(>select)": {
    marginTop: 0,
  },
  '[type="submit"]': {
    marginTop: "1.5rem",
  },
});

type WithSelected = { selected: boolean };
const PostTypeButton = styled(Button)<WithSelected>(({ selected, theme }) => ({
  [theme.breakpoints.maxTablet]: {
    paddingLeft: ".7em",
    paddingRight: ".7em",
  },
  borderRadius: 0,
  flex: "0 0 100%",
  "&:first-of-type": { borderRadius: "1.33em 1.33em 0 0" },
  "&:last-of-type": { borderRadius: "0 0 1.33em 1.33em" },
  "button+&": {
    marginTop: -2,
  },
  [theme.breakpoints.xs]: {
    flex: "0 0 auto",
    "&:first-of-type": { borderRadius: "99px 0 0 99px" },
    "&:last-of-type": { borderRadius: "0 99px 99px 0" },
    "button+&": {
      marginTop: 0,
      marginLeft: -2,
    },
  },
  "&:hover, &:focus-visible": {
    background: theme.colors.fg20,
    color: theme.colors.fg,
  },
  ...(selected && {
    background: theme.colors.fg,
    "&&": { color: theme.colors.bg },
    "&&:hover, &&:focus-visible": {
      background: theme.colors.fg,
      color: theme.colors.bg,
    },
  }),
  lineHeight: 1,
  svg: {
    maxWidth: "1.3em",
    maxHeight: "1.3em",
  },
}));
PostTypeButton.defaultProps = { variant: "secondary", size: "xs" };

const TwoColumnForm = styled.div(({ theme }) => ({
  alignItems: "start",
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "1rem",
  '[type="number"], [type="text"], [type="date"], select, *:has(>select)': {
    height: 44,
    width: "100%",
  },
  [theme.breakpoints.xs]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));

const SSuccessMessage = styled(SuccessMessage)({
  position: "fixed",
  bottom: "45%",
  left: "50%",
  padding: "1.5em 2em",
  fontWeight: "bold",
  "@media (min-width: 660px)": {
    bottom: "45%",
  },
});

const getTitleLabel = (postType: PostType) => {
  let label = "Title";
  if (postType === "ANNOUNCEMENT") label = "Announcement Title";
  else if (postType === "EVENT") label = "Event Title";
  else if (["SALE", "GIVEAWAY"].includes(postType)) label = "Item Name";
  return label;
};

const getTextLabel = (postType: PostType) => {
  let label = "Your Update";
  if (postType === "ANNOUNCEMENT") label = "Announcement Details";
  else if (postType === "EVENT") label = "Event Details";
  else if (["SALE", "GIVEAWAY"].includes(postType)) label = "Item Description";
  return label;
};

const getImageLabel = (postType: PostType) => {
  let label: React.ReactNode = "Image";
  if (postType === "SALE") {
    label = (
      <>
        Item Image <RequiredText />
      </>
    );
  }
  return label;
};

const getLinkLabel = (postType: PostType) => {
  let label: React.ReactNode = "Link";
  if (postType === "SALE") {
    label = (
      <>
        URL to Purchase <RequiredText />
      </>
    );
  }
  return label;
};

export { PostForm };
